import React, { useState, useEffect, useRef } from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  FlagIcon,
  ChatBubbleLeftIcon,
  LinkIcon,
  ClipboardDocumentIcon,PaperClipIcon,
} from "@heroicons/react/24/outline";

const statusConfig = {
  Pending: { icon: <ClockIcon className="w-4 h-4" />, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  "In Progress": { icon: <ClockIcon className="w-4 h-4" />, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  "Needs Approval": { icon: <ExclamationTriangleIcon className="w-4 h-4" />, color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
  Completed: { icon: <CheckCircleIcon className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  Approved: { icon: <CheckCircleIcon className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  Rejected: { icon: <XMarkIcon className="w-4 h-4" />, color: "text-red-600", bg: "bg-red-50 border-red-200" },
};

export default function TaskDetailsDialog({ task, open, onClose,user, currentUserId, isManager, onApprove,mode = "view", // NEW: "view" | "complete"
  onComplete, }) {
  const [comment, setComment] = useState("");
  const [proofFiles, setProofFiles] = useState([]);
  const [proofUrls, setProofUrls] = useState([""]);
  const panelRef = useRef(null);
  const isCompletionMode = mode === "complete";
  // const canComplete = ["Pending", "In Progress"].includes(task?.status) && task?.assigned_to === currentUserId|| 
  //  (task?.assigned_team_id && currentUserId && task.assigned_team_id === user.team_id);;
  const canComplete =
  ["Pending", "In Progress"].includes(task?.status) &&
  (task?.assigned_to === user?.id ||
   (task?.assigned_team_id && user?.team_id === task.assigned_team_id));
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  if (!task || !open) return null;

  const formatDate = (date) => (date ? new Date(date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
  const formatDueDate = (date) => (date ? new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");

  const canApprove = isManager || task.approver_id === currentUserId;
const handleFileProof = (e) => {
    const files = Array.from(e.target.files);
    setProofFiles(prev => [...prev, ...files]);
  };

  const removeProofFile = (index) => {
    setProofFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addProofUrl = () => setProofUrls(prev => [...prev, ""]);
  const updateProofUrl = (i, val) => setProofUrls(prev => prev.map((u, idx) => idx === i ? val : u));
  const removeProofUrl = (i) => setProofUrls(prev => prev.filter((_, idx) => idx !== i));

  const handleTaskComplete = async () => {
    if (!onComplete) return;

    const formData = new FormData();

    proofFiles.forEach(file => formData.append("proof", file));
    const validUrls = proofUrls.filter(u => u.trim());
    validUrls.forEach(url => formData.append("proof_urls", url.trim()));

    await onComplete(task.id, formData);
    onClose();
  };
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Slide Panel */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-gray-900 shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header – slim */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-5 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate max-w-[280px]">
              {task.task_uid} — {task.title}
            </h2>
            <div className="mt-1 flex items-center gap-3 text-sm">
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  statusConfig[task.status]?.bg || "bg-gray-100 border-gray-200"
                } ${statusConfig[task.status]?.color || "text-gray-700"}`}
              >
                {statusConfig[task.status]?.icon}
                {task.status}
              </div>
              <span className="text-gray-600 dark:text-gray-400">
                {task.priority || "Meium"}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Main Content – compact */}
        <div className="p-5 space-y-6">
          {/* Related Lead */}
          {(task.Lead || task.related_lead || task.lead_name || task.lead_uid) && (
            <div className="bg-blue-50/60 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <LinkIcon className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mapped</p>
                  <p className="font-semibold text-base text-blue-900 dark:text-blue-200">
                    {task.Lead?.lead_uid || task.lead_uid || "—"} — {task.Lead?.name || task.lead_name || "—"}
                  </p>
                  {(task.Lead?.mobile || task.lead_mobile) && (
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
                      Mobile: {task.Lead?.mobile || task.lead_mobile}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
{(task.related_project || task.project || task.project_name || task.project_uid) && (
  <div className="bg-blue-50/60 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <LinkIcon className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mapped</p>
        <p className="font-semibold text-base text-blue-900 dark:text-blue-200">
          {task.project_uid || task.related_project?.project_uid || "—"} — {task.project_name || task.related_project?.name || "—"}
        </p>
      </div>
    </div>
  </div>
)}

          {/* Module */}
          <div className="flex items-center gap-3 text-sm">
            <ClipboardDocumentIcon className="w-6 h-6 text-gray-500" />
            <div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Module</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {task.module || (task.lead_id ? "Lead" : "General")}
              </p>
            </div>
          </div>

          {/* Dates – side by side */}
          <div className="grid grid-cols-2 gap-5 text-sm">
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Start</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {task.start_date ? formatDueDate(task.start_date) : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Due</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDueDate(task.due_date)}
                </p>
              </div>
            </div>
          </div>

          {/* People – compact grid */}
          <div className="grid grid-cols-3 gap-5 text-sm">
            <div>
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                <UserIcon className="w-4 h-4" />
                <span className="font-medium">Created</span>
              </div>
              <p className="font-semibold">{task.creator?.name || task.creator_name || "—"}</p>
              <p className="text-xs text-gray-500 mt-0.5">{formatDate(task.created_at)}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                <UserIcon className="w-4 h-4" />
                <span className="font-medium">Assigned</span>
              </div>
              <p className="font-semibold">{task.assignee_name || "Unassigned"}</p>
            </div>

            {task.requires_approval && (
              <div>
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <FlagIcon className="w-4 h-4" />
                  <span className="font-medium">Approver</span>
                </div>
                <p className="font-semibold">
                  {task.plannedApprover?.name || task.approver_name || "—"}
                </p>
              </div>
            )}
          </div>

          {/* Department */}
          <div className="flex items-center gap-3 text-sm">
            <div className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500">
              <span className="text-xs font-bold">D</span>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 font-medium">Department</p>
              <p className="font-semibold text-gray-900 dark:text-white">{task.department || "—"}</p>
            </div>
          </div>

          {/* Notes */}
          {task.notes && (
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                <ChatBubbleLeftIcon className="w-5 h-5" />
                <span className="font-medium">Notes</span>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm leading-relaxed">
                {task.notes}
              </div>
            </div>
          )}
          {/* ATTACHMENTS - THIS WILL APPEAR NOW */}
          {task.attachments && task.attachments.length > 0 ? (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <PaperClipIcon className="w-6 h-6 text-gray-600" />
                Attachments ({task.attachments.length})
              </h3>
              <div className="space-y-4">
                {task.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {att.type === "file" ? (
                      <>
                        <ClipboardDocumentIcon className="w-10 h-10 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <a
                            href={att.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline break-all"
                          >
                            📎 {att.original_name || "Download File"}
                          </a>
                          <p className="text-xs text-gray-500 mt-2">
                            Uploaded by <span className="font-medium">{att.uploaded_by || "Unknown"}</span> on{" "}
                            {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString("en-GB") : "—"}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-10 h-10 text-green-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-green-600 dark:text-green-400 hover:underline break-all"
                          >
                            🔗 {att.url}
                          </a>
                          <p className="text-xs text-gray-500 mt-2">
                            Added by <span className="font-medium">{att.uploaded_by || "Unknown"}</span> on{" "}
                            {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString("en-GB") : "—"}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* COMPLETION PROOF SECTION - Only in completion mode */}
          {isCompletionMode && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Add Completion Proof (Optional)
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload Proof Files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileProof}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded file:bg-blue-50 file:text-blue-700"
                  />
                  {proofFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {proofFiles.map((file, i) => (
                        <div key={i} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                          <span className="text-sm truncate">{file.name}</span>
                          <button onClick={() => removeProofFile(i)} className="text-red-600 text-sm">Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Add Proof Links
                    </label>
                    <button onClick={addProofUrl} className="text-blue-600 text-sm">+ Add URL</button>
                  </div>
                  {proofUrls.map((url, i) => (
                    <div key={i} className="flex gap-3 mb-3">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => updateProofUrl(i, e.target.value)}
                        placeholder="https://example.com/screenshot"
                        className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
                      />
                      {proofUrls.length > 1 && (
                        <button onClick={() => removeProofUrl(i)} className="px-4 text-red-600">Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* Timeline – compact */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Timeline</h3>
            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                  </div>
                  <div className="w-0.5 bg-gray-200 dark:bg-gray-700 flex-1 mt-1"></div>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Task Created</p>
                  <p className="text-sm text-gray-500">{formatDate(task.created_at)}</p>
                  <p className="text-xs text-gray-500">By {task.creator_name || "Unknown"}</p>
                </div>
              </div> 

              {task.assigned_at && (
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                    </div>
                    <div className="w-0.5 bg-gray-200 dark:bg-gray-700 flex-1 mt-1"></div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Task Assigned</p>
                    <p className="text-sm text-gray-500">{formatDate(task.assigned_at)}</p>
                    <p className="text-xs text-gray-500">To {task.assignee_name || "Unassigned"}</p>
                  </div>
                </div>
              )}

              {task.completed_at && (
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Marked Complete</p>
                    <p className="text-sm text-gray-500">{formatDate(task.completed_at)}</p>
                    <p className="text-xs text-gray-500">By {task.assignee_name}</p>
                  </div>
                </div>
              )}

              {task.approved_at && (
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        task.status === "Rejected" ? "bg-red-100" : "bg-green-100"
                      } dark:${task.status === "Rejected" ? "bg-red-900/40" : "bg-green-900/40"}`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          task.status === "Rejected" ? "bg-red-600" : "bg-green-600"
                        }`}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {task.status === "Rejected" ? "Task Rejected" : "Task Approved"}
                    </p>
                    <p className="text-sm text-gray-500">{formatDate(task.approved_at)}</p>
                    <p className="text-xs text-gray-500">By {task.approver_name || "Manager"}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-5 py-4">
          {isCompletionMode ? (
            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="px-6 py-3 border border-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleTaskComplete}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow transition"
              >
                Complete Task
              </button>
            </div>) :
          task.status === "Needs Approval" && canApprove ? (
            <div className="space-y-4">
              <textarea
                rows={3}
                placeholder="Add comment (optional)..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 resize-none"
              />
              <div className="flex justify-end gap-3">
                {/* <button
                  onClick={() => onApprove(false, comment)}
                  className="px-6 py-2.5 text-sm border border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg font-medium transition"
                >
                  Reject
                </button> */}
                <button
  onClick={() => onApprove(false, comment)}
  className="px-6 py-2.5 text-sm border border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg font-medium transition"
>
  Reject & Send Back
</button>
                <button
                  onClick={() => onApprove(true, comment)}
                  className="px-6 py-2.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-sm transition"
                >
                  Approve
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-8 py-2.5 text-sm bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

